import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { Reveal } from '@/components/landing/motion';

/**
 * Shared layout for the long-form public pages (legal, security, oss,
 * changelog). Single column of prose on the warm-paper background, with
 * the brand chip in the header and a "last updated" line below the
 * title. Distinct from the marketing pages — these are reading
 * surfaces, not selling surfaces.
 */
export function LegalLayout({
  eyebrow,
  title,
  updated,
  children,
}: {
  eyebrow: string;
  title: ReactNode;
  /** ISO date string of the last meaningful change. */
  updated: string;
  children: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const formatted = new Date(updated).toLocaleDateString(lang, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return (
    <div className="min-h-screen paper-archive">
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-5 sm:px-6 pt-12 md:pt-16 pb-16">
        <header>
          <p className="text-[11px] uppercase tracking-[0.18em] text-accent-600 font-semibold">
            {eyebrow}
          </p>
          <h1 className="mt-3 display serif-jp text-4xl md:text-5xl font-semibold tracking-tight text-ink-900 leading-[1.05] text-balance">
            {title}
          </h1>
          <p className="mt-3 text-[12px] text-ink-500 font-mono">
            {t('legal.updated', { defaultValue: 'Last updated' })} · {formatted}
          </p>
        </header>

        <article className="mt-10 card-paper p-8 sm:p-10 prose-knowlez">
          {children}
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}

/**
 * Section anchor — gets a small visible kicker + a tappable id.
 * Used so the table of contents in long pages can deep-link.
 */
export function Section({ id, kicker, title, children }: {
  id: string;
  kicker?: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <Reveal>
      <section id={id} className="mt-10 first:mt-0 scroll-mt-24">
        {kicker && (
          <p className="text-[10px] uppercase tracking-[0.18em] text-ink-400 font-mono font-semibold mb-1">
            {kicker}
          </p>
        )}
        <h2 className="display text-[19px] sm:text-[21px] font-semibold text-ink-900 tracking-tight">
          {title}
        </h2>
        <div className="mt-3 space-y-3 text-[14.5px] text-ink-700 leading-relaxed">
          {children}
        </div>
      </section>
    </Reveal>
  );
}

/** Tight bullet list — design-system styled. */
export function Bullets({ children }: { children: ReactNode }) {
  return <ul className="space-y-1.5 list-none pl-0">{children}</ul>;
}

export function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2.5 items-start">
      <span className="mt-2 inline-block h-1 w-1 rounded-full bg-accent-500 shrink-0" />
      <span>{children}</span>
    </li>
  );
}

